'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useState } from 'react';

import { earnStatisticsBunja, earnSummaryBunja } from '@flipster/features/src/shared/earn/state';
import { FormattedMessage } from '@flipster/i18n';
import { createSingleChoice } from '@flipster/react/design-system/chip';
import { css } from '@flipster/styled-system/css';
import { hstack } from '@flipster/styled-system/patterns';
import { PusdtCurrency, PusdtDisplayName } from '@flipster/utils/constants/currency';

import { EarnHistory } from './earn-history';
import { HistoryLink } from './history-link';

type EarnHistoryTabType = 'total' | (string & {});
const Choice = createSingleChoice<EarnHistoryTabType>();

export const EarnHistorySection = () => {
  const { earnSummaryAtom } = useBunja(earnSummaryBunja);
  const { earnStatisticsAtom } = useBunja(earnStatisticsBunja);
  const earnSummary = useAtomValue(earnSummaryAtom);
  const earnStatistics = useAtomValue(earnStatisticsAtom);

  // based on earnStatistics's currencies, but filtered by earnSummary's currencies
  const summaryCurrencies = new Set(earnSummary?.currencies.map((c) => c.currency) ?? []);
  const tabItems = [
    { label: 'Total', value: 'total' },
    ...(earnStatistics?.currencies
      .filter((c) => summaryCurrencies.has(c.currency))
      .map(({ currency }) => ({
        label: currency === PusdtCurrency ? PusdtDisplayName : currency,
        value: currency,
      })) ?? []),
  ];

  const [tab, setTab] = useState<string>('total');

  return (
    <section>
      <div className={hstack({ justify: 'space-between', mb: 16 })}>
        <h2 className={css({ textStyle: 'Title/S', color: 'content.default.level-5' })}>
          <FormattedMessage id='Autogen_common.r251126.s_History' />
        </h2>
        <HistoryLink tab={tab} />
      </div>
      <Choice value={tab} onChange={setTab} css={{ overflowX: 'auto', mx: -16, px: 16, mb: 16 }}>
        {tabItems.map(({ label, value }) => (
          <Choice.Chip key={value} value={value} label={label} />
        ))}
      </Choice>
      <EarnHistory
        label={tabItems.find((t) => t.value === tab)?.label ?? ''}
        currency={tab === 'total' ? undefined : tab}
      />
    </section>
  );
};
